import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import type { Permission } from '@/auth/permissions';
import { ErrorState } from '@/components/states';
import type { RouterContext } from './layout-routes';
import { NoAccessPage } from './pages/status-pages';

/** Thrown by `requirePermissions`; the route error boundary shows the no-access page (never a logout). */
export class NoAccessError extends Error {
  constructor(readonly permissions: readonly Permission[]) {
    super(`Missing permission: ${permissions.join(', ')}`);
    this.name = 'NoAccessError';
  }
}

/** For a route's `beforeLoad`: `beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_CURRENCIES')`. */
export function requirePermissions(context: RouterContext, ...permissions: Permission[]) {
  if (!context.auth.can(...permissions)) throw new NoAccessError(permissions);
}

/** The router's default error component: no-access for guards, otherwise a retryable error state. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  if (error instanceof NoAccessError) return <NoAccessPage />;
  return (
    <div className="mx-auto max-w-lg py-16">
      <ErrorState
        error={error}
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
      />
    </div>
  );
}
